import { useState, useEffect } from 'react';
import API from '../utils/api';
import {
  Bell, Moon, Shield, LogOut, ChevronRight,
  Copy, CheckCircle, AlertCircle, X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import BottomNav from '../components/BottomNav';

export default function SettingsPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState(() => {
    return localStorage.getItem('notifications') === 'true' || true;
  });
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [copied, setCopied] = useState(false);

  // Change PIN Modal
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinStep, setPinStep] = useState(1); // 1 = enter, 2 = confirm
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const res = await API.get('/user/dashboard');
        setUser(res.data.user);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  // Persist settings
  useEffect(() => {
    localStorage.setItem('notifications', notifications);
  }, [notifications]);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const copyUID = async () => {
    if (!user?._id) return;
    try {
      await navigator.clipboard.writeText(user._id);
      setCopied(true);
      toast.success('UID copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handlePinInput = (digit) => {
    if (pinStep === 1 && pin.length < 6) {
      setPin(pin + digit);
    } else if (pinStep === 2 && confirmPin.length < 6) {
      setConfirmPin(confirmPin + digit);
    }
  };

  const handlePinDelete = () => {
    if (pinStep === 1 && pin.length > 0) {
      setPin(pin.slice(0, -1));
    } else if (pinStep === 2 && confirmPin.length > 0) {
      setConfirmPin(confirmPin.slice(0, -1));
    }
  };

  const submitPin = async () => {
    if (pin !== confirmPin) {
      setPinError('PINs do not match');
      return;
    }
    try {
      await API.put('/user/pin', { pin });
      setPinSuccess(true);
      toast.success('PIN updated!');
      setTimeout(() => {
        setShowPinModal(false);
        resetPinModal();
      }, 1500);
    } catch (err) {
      setPinError(err.response?.data?.error || 'Failed to update PIN');
    }
  };

  const resetPinModal = () => {
    setPin('');
    setConfirmPin('');
    setPinStep(1);
    setPinError('');
    setPinSuccess(false);
  };

  const openPinModal = () => {
    resetPinModal();
    setShowPinModal(true);
  };

  const handleLogout = () => {
    // Add your logout logic here
    toast.success('Logged out');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-sm">
          <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-3" />
          <p className="text-red-800 font-medium">Error</p>
          <p className="text-sm text-red-600 mt-1">{error || 'No user data'}</p>
        </div>
      </div>
    );
  }

  const menuItems = [
    { icon: <Bell className="w-5 h-5" />, label: 'Notifications', value: notifications ? 'On' : 'Off', toggle: () => setNotifications(v => !v) },
    { icon: <Moon className="w-5 h-5" />, label: 'Dark Mode', value: darkMode ? 'On' : 'Off', toggle: () => setDarkMode(v => !v) },
    { icon: <Shield className="w-5 h-5" />, label: 'Change PIN', value: 'Update transaction PIN', onClick: openPinModal },
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-20 p-4 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary mb-2">Settings</h1>
            <p className="text-gray-600">Manage your account</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-indigo-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                {user.name?.charAt(0) || '?'}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{user.name || 'User'}</h2>
                <p className="text-sm text-gray-600">{user.email || 'email@example.com'}</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Your UID</p>
                <p className="font-mono text-sm text-gray-800">
                  {user._id ? `${user._id.slice(0, 8)}...${user._id.slice(-4)}` : 'N/A'}
                </p>
              </div>
              <button onClick={copyUID} className="p-2 hover:bg-gray-200 rounded-lg transition" aria-label="Copy UID">
                {copied ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {menuItems.map((item, i) => (
              <button
                key={i}
                onClick={item.onClick || item.toggle}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition border-b border-gray-100 last:border-0"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">{item.icon}</div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.value}</p>
                  </div>
                </div>
                {(item.toggle || item.onClick) && <ChevronRight className="w-5 h-5 text-gray-400" />}
              </button>
            ))}
          </div>

          <button
            onClick={handleLogout}
            className="w-full py-4 bg-red-50 text-red-600 rounded-2xl font-bold hover:bg-red-100 transition flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" /> Log Out
          </button>

          <p className="text-center text-xs text-gray-500 mt-8">App v1.0.0</p>
        </div>
      </div>

      {/* Change PIN Modal */}
      {showPinModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={(e) => e.target === e.currentTarget && setShowPinModal(false)}
        >
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Change PIN</h3>
              <button onClick={() => setShowPinModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {pinSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="text-green-600 font-medium">PIN updated successfully!</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  {pinStep === 1 ? 'Enter new 6-digit PIN' : 'Confirm your PIN'}
                </p>

                <div className="flex justify-center gap-2 mb-6">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center text-lg font-mono"
                    >
                      {(pinStep === 1 ? pin : confirmPin)[i] || ''}
                    </div>
                  ))}
                </div>

                {pinError && <p className="text-red-600 text-sm text-center mb-4">{pinError}</p>}

                <div className="grid grid-cols-3 gap-3">
                  {[1,2,3,4,5,6,7,8,9].map(n => (
                    <button
                      key={n}
                      onClick={() => handlePinInput(n.toString())}
                      className="h-14 rounded-xl bg-gray-100 hover:bg-gray-200 font-bold text-lg transition"
                    >
                      {n}
                    </button>
                  ))}
                  <div />
                  <button
                    onClick={() => handlePinInput('0')}
                    className="h-14 rounded-xl bg-gray-100 hover:bg-gray-200 font-bold text-lg transition"
                  >
                    0
                  </button>
                  <button
                    onClick={handlePinDelete}
                    className="h-14 rounded-xl bg-gray-100 hover:bg-gray-200 font-bold text-lg transition"
                  >
                    ←
                  </button>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setShowPinModal(false)}
                    className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => pinStep === 1 ? setPinStep(2) : submitPin()}
                    disabled={(pinStep === 1 && pin.length < 6) || (pinStep === 2 && confirmPin.length < 6)}
                    className="flex-1 py-3 rounded-xl bg-primary text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {pinStep === 1 ? 'Next' : 'Confirm'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <BottomNav />
    </>
  );
}